import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import parse from "html-react-parser";
import * as React from "react";
import { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ResourceInfoLinkFieldValue {
    type: "InfoLink";
    value: string;
}

interface ResourceInfoCardModel {
    fields: {
        image: string;
        title: string;
        description: string;
        link: ResourceInfoLinkFieldValue;
        linkTitle: string;
        openInNewWindow: boolean;
        isDownloadable: boolean;
        pdfLink: string;
        buttonLabel: string;
    };
}

const enum fields {
    ResourceinfoCards = "infoCards",
    image = "image",
    title = "title",
    description = "description",
    link = "link",
    linkTitle = "linkTitle",
    openInNewWindow = "openInNewWindow",
    isDownloadable = "isDownloadable",
    pdfLink = "pdfLink",
    buttonLabel = "buttonLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.ResourceinfoCards]: ResourceInfoCardModel[];
    };
}
export interface MappedLink {
    title?: string;
    url?: string;
    image?: string;
    description?: string;
    link?: ResourceInfoLinkFieldValue;
    linkTitle?: string;
    openInNewWindow?: boolean;
    isDownloadable?: boolean;
    pdfLink?: string;
    buttonLabel?: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { infoCards },
    } = ownProps;
    const mappedInfoCards = [];
    for (const card of infoCards) {
        let mappedLink: MappedLink | undefined;
        const readonlyPageLink = getPageLinkByNodeId(state, card.fields.link.value);
        if (readonlyPageLink) {
            mappedLink = { ...card.fields };
            mappedLink.url = readonlyPageLink.url;
        } else {
            mappedLink = { ...card.fields };
            mappedLink.url = (mappedLink.link?.value)?(mappedLink.link?.value):"";
        }
        mappedInfoCards.push(mappedLink);
    }
    return {
        mappedInfoCards,
    };
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ResourceDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subResourceItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subResourceImageItem?: GridItemProps;
    subResourceImageClickable?: ClickablePresentationProps;
    subResourceImage?: LazyImageProps;
    subResourceNameLinkItem?: GridItemProps;
    subResourceNameLink?: LinkPresentationProps;
    PLPheaderText?: TypographyPresentationProps;
    PLPdescriptionsText?: TypographyPresentationProps;
}

export const ResourceDetailSubCategoriesStyles: ResourceDetailSubCategoriesStyles = {
    container: {
        gap: 0,
    },
    subResourceItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-bottom: 30px;
        `,
    },
    innerContainer: {
        gap: 0,
        css: css`
            border: 1px solid #e1e1e1;
            background-color: #ffffff;
            margin: 0 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 0 29px;
                        `,
                        css`
                            margin: 0 29px;
                        `,
                        css`
                            border: 1px solid #e1e1e1;
                            background-color: #ffffff;
                            margin: 0 29px;
                        `,
                        css`
                            border: 1px solid #e1e1e1;
                            background-color: #ffffff;
                            margin: 0 0px;
                        `,
                        css`
                            border: 1px solid #e1e1e1;
                            background-color: #ffffff;
                            margin: 0 0px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceImageItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            width: 100%;
            padding: 0px;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 20.5px;
                        `,
                        css`
                            padding: 20.5px;
                        `,
                        css`
                            padding: 20.5px;
                        `,
                        css`
                            padding: 0;
                        `,
                        css`
                            padding: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceImageClickable: {
        css: css`
            width: 100%;
            cursor: default;
        `,
    },
    subResourceImage: {
        css: css`
            height: auto;
            width: 100%;
            img {
                height: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 198px;
                            img {
                                height: auto;
                            }
                        `,
                        css`
                            height: 198px;
                            img {
                                height: auto;
                            }
                        `,
                        css`
                            height: 198px;
                            img {
                                height: auto;
                            }
                        `,
                        css`
                            height: auto;
                            img {
                                height: 100%;
                            }
                        `,
                        css`
                            height: auto;
                            img {
                                height: 100%;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceNameLinkItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            padding-left: 51px;
            padding-right: 48.5px;
            span {
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 20.5px;
                        padding-right: 20.5px;
                        margin-bottom: 25.5px !important;
                    `,
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 20.5px;
                        padding-right: 20.5px;
                        margin-bottom: 25.5px !important;
                    `,
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 20.5px;
                        padding-right: 20.5px;
                        margin-bottom: 25.5px !important;
                    `,
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 51px;
                        padding-right: 48.5px;
                        margin-bottom: 0px !important;
                    `,
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 51px;
                        padding-right: 48.5px;
                        margin-bottom: 0px !important;
                    `,
                ])}
        `,
    },
    PLPheaderText: {
        css: css`
            font-size: 30px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 36px;
            color: #33333a;
            text-align: left;
        `,
    },
    PLPdescriptionsText: {
        css: css`
            font-size: 14px;
            letter-spacing: 0;
            line-height: 25px;
            margin-top: 12px;
            text-align: left;
            body {
                font-size: 14px;
                letter-spacing: 0;
                line-height: 25px;
                margin-top: 12px;
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 6px;
                        `,
                        css`
                            margin-top: 6px;
                        `,
                        css`
                            margin-top: 6px;
                        `,
                        css`
                            margin-top: 12px;
                        `,
                        css`
                            margin-top: 12px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            css: css`
                width: 100%;
                text-align: center;
            `,
        },
    },
};

const styles = ResourceDetailSubCategoriesStyles;

const ResourceDetailSubCategories: React.FC<Props> = ({ fields, mappedInfoCards }) => {
    return (
        <GridContainer {...styles.container}>
            {mappedInfoCards.map((card, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <GridItem key={index} {...styles.subResourceItem}>
                    <GridContainer {...styles.innerContainer}>
                        {card && card.image && (
                            <GridItem {...styles.subResourceImageItem}>
                                <Clickable href={card.url} {...styles.subResourceImageClickable}>
                                    <LazyImage src={card.image} {...styles.subResourceImage} />
                                </Clickable>
                            </GridItem>
                        )}
                        <GridItem {...styles.subResourceNameLinkItem}>
                            <StyledWrapper {...styles.subResourceNameLink}>
                                {card && card.title && (
                                    <Typography as="h3" {...styles.PLPheaderText}>
                                        {card.title}
                                    </Typography>
                                )}

                                {card && card.description && (
                                    <Typography as="p" {...styles.PLPdescriptionsText}>
                                        {card.description}
                                    </Typography>
                                )}
                                {card && (
                                    <Link
                                        href={card.isDownloadable ? card.pdfLink : card.url}
                                        target={card.openInNewWindow || card.isDownloadable ? "_blank" : ""}
                                        download={!!card.isDownloadable}
                                    >
                                        <Button
                                            css={css`
                                                span {
                                                    color: #ffffff;
                                                    font-size: 12px;
                                                    font-weight: bold;
                                                    letter-spacing: 2px;
                                                    line-height: 15px;
                                                    display: inline-block;
                                                    text-align: center !important;
                                                }
                                                &:hover {
                                                    background: #000000 !important;
                                                    border-color: #000000 !important;
                                                }

                                                text-align: center;
                                                height: auto;
                                                width: 180px;
                                                padding : 8px 20px;
                                                ${({ theme }: { theme: BaseTheme }) =>
                                                    breakpointMediaQueries(
                                                        theme,
                                                        [
                                                            css`
                                                                margin-top: 20px;
                                                            `,
                                                            css`
                                                                margin-top: 20px;
                                                            `,
                                                            css`
                                                                margin-top: 20px;
                                                            `,
                                                            css`
                                                                margin-top: 25px;
                                                            `,
                                                            css`
                                                                margin-top: 25px;
                                                            `,
                                                        ],
                                                        "min",
                                                    )}
                                            `}
                                        >
                                            {card.buttonLabel}
                                        </Button>
                                    </Link>
                                )}
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            ))}
        </GridContainer>
    );
};

const membersList: WidgetModule = {
    component: connect(mapStateToProps)(ResourceDetailSubCategories),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.ResourceinfoCards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: fields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: fields.image,
                        displayName: "Photo",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 1,
                    },
                    {
                        name: fields.description,
                        displayName: "Description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                        sortOrder: 6,
                    },
                    {
                        name: fields.openInNewWindow,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        sortOrder: 5,
                    },
                    {
                        name: fields.isDownloadable,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        sortOrder: 5,
                    },
                    {
                        name: fields.link,
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        allowCategories: item => false,
                        sortOrder: 4,
                        isVisible: item => item.fields.isDownloadable === false,
                    },
                    {
                        name: fields.pdfLink,
                        displayName: "Downloadable pdf",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: item => item.fields.isDownloadable === true,
                        sortOrder: 7,
                    },
                    {
                        name: fields.buttonLabel,
                        displayName: "Add Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 8,
                    },
                ],
            },
        ],
    },
};

export default membersList;
